<template>
  <woot-modal :show.sync="show" :on-close="onClose">
    <div class="generate-agent-response-modal h-auto overflow-auto flex flex-col bg-white dark:bg-slate-800">
      <woot-modal-header
        :header-title="$t('CONVERSATION.AI_GENERATE_RESPONSE.TITLE')"
        :header-content="generatedContent ? '' : $t('CONVERSATION.AI_GENERATE_RESPONSE.DESC')"
      />
      <form class="w-full modal-content p-4" @submit.prevent="onGenerate">
        <!-- Query section: only when no generated content yet -->
        <div v-if="!generatedContent" class="w-full mb-4">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {{ $t('CONVERSATION.AI_GENERATE_RESPONSE.QUERY_LABEL') }}
          </label>
          <textarea
            v-model="query"
            class="generate-agent-response-modal__textarea"
            :placeholder="$t('CONVERSATION.AI_GENERATE_RESPONSE.QUERY_PLACEHOLDER')"
            rows="3"
          />
        </div>
        <!-- Generated content: editable when we have response -->
        <div v-else class="w-full mb-4">
          <textarea
            v-model="editableContent"
            class="generate-agent-response-modal__textarea generate-agent-response-modal__result"
            :rows="resultTextareaRows"
          />
        </div>
        <div class="flex flex-row justify-end gap-2 py-2">
          <woot-button variant="clear" :disabled="loading" @click.prevent="onClose">
            {{ $t('CONVERSATION.AI_GENERATE_RESPONSE.CANCEL') }}
          </woot-button>
          <template v-if="!generatedContent">
            <woot-button :disabled="loading" @click.prevent="onGenerate">
              <span v-if="loading" class="inline-flex items-center gap-1">
                <span class="generate-agent-response-modal__btn-spinner" />
                {{ $t('CONVERSATION.AI_GENERATE_RESPONSE.GENERATE') }}
              </span>
              <span v-else>{{ $t('CONVERSATION.AI_GENERATE_RESPONSE.GENERATE') }}</span>
            </woot-button>
          </template>
          <woot-button v-else @click.prevent="onSend">
            {{ $t('CONVERSATION.AI_GENERATE_RESPONSE.SEND') }}
          </woot-button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';

export default {
  name: 'GenerateAgentResponseModal',
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    generatedContent: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      query: '',
      editableContent: '',
    };
  },
  computed: {
    resultTextareaRows() {
      const text = this.editableContent || '';
      const lines = text.split(/\n/).length;
      return Math.min(20, Math.max(4, lines + 1));
    },
  },
  watch: {
    generatedContent: {
      handler(val) {
        this.editableContent = val || '';
      },
      immediate: true,
    },
  },
  methods: {
    onClose() {
      this.query = '';
      this.editableContent = '';
      this.$emit('update:show', false);
      this.$emit('close');
    },
    onGenerate() {
      if (this.loading) return;
      this.$emit('submit', { query: this.query.trim() || '' });
    },
    onSend() {
      const content = (this.editableContent || '').trim();
      if (content) {
        this.$emit('send', { content });
        this.onClose();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.generate-agent-response-modal {
  border-radius: 12px;
  overflow: hidden;
}

.generate-agent-response-modal__textarea {
  width: 100%;
  min-height: 80px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  font-size: 14px;
  transition: border-color 0.15s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #6366f1;
  }
}

.generate-agent-response-modal__result {
  min-height: 6em;
  resize: vertical;
  box-sizing: border-box;
}

.generate-agent-response-modal__btn-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: generate-agent-spin 0.7s linear infinite;
}

@keyframes generate-agent-spin {
  to {
    transform: rotate(360deg);
  }
}

.dark .generate-agent-response-modal__textarea {
  border-color: #475569;
  background: #1e293b;
  color: #f1f5f9;

  &::placeholder {
    color: #64748b;
  }
}
</style>
